import React, { useState, useEffect } from "react";
import { parse } from "node-html-parser";
import { NextSeo } from "next-seo";
import Head from "next/head";
import marked from "marked";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app";
import { keyframes } from "@mui/system";
import Image from "next/image";
import dayjs from "dayjs";
// components
import InterestRateChart from "components/InterestRateChart";
import BlogSidebarActions from "components/blog/BlogSidebarActions";
import MuiMarkupWrap from "components/global/MuiMarkupWrap";
import TableOfContent from "components/TableOfContent";
import MuiIndexMarkupWrap from "components/global/MuiIndexMarkupWrap";
import QuoteTeaser from "components/QuoteTeaser";
import QuoteCTA from "components/QuoteCTA";
import BlogCard from "components/blog/BlogCard";
import EscrowCalculator from "components/Calculators/EscrowCalculator";
// mui
import Link from "@mui/material/Link";
import ButtonBase from "@mui/material/ButtonBase";
import { FormControlLabel } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import QuoteStepper from "components/Loans/QuoteStepper";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/styles";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
// lib
import statesAbbreviations from "lib/statesAbbreviations";
import publicMetaImages from "lib/publicMetaImages";
import dscrLoanStateData from "lib/dscrLoanStateData";
// utils
import formatMoney from "lib/formatMoney";
import scroller from "lib/scroller";
import calculatorTeasersURLs from "components/Calculators/calculatorTeasersURLs";
import calculatorURLs from "components/Calculators/calculatorURLs";
import dscrLoanStatePages from "components/blog/dscrLoanStatePages";
import DSCRCalculator from "components/Calculators/DSCRCalculator";
import LandLoanCalculator from "components/Calculators/LandLoanCalculator";
import CashOfferCalculator from "components/Calculators/CashOfferCalculator";
//
import CashOnCashReturnCalculator from "components/Calculators/CashOnCashReturnCalculator";
import CashFlowCalculator from "components/Calculators/CashFlowCalculator";
import ProratedRentCalculator from "components/Calculators/ProratedRentCalculator";
import CashOutRefinanceCalculator from "components/Calculators/CashOutRefinanceCalculator";
import IRRCalculator from "components/Calculators/IRRCalculator";
import RentalPropertyCalculator from "components/Calculators/RentalPropertyCalculator";
import ClosingCostsCalculator from "components/Calculators/ClosingCostCalculator";
import DTICalculator from "components/Calculators/DTICalculator";

export async function getStaticPaths() {
  const targetUrlBlogs = `${process.env.CMS_URL}/blog-posts?_sort=updated_at:DESC&_limit=-1`; // reduced from -1(unlimited) to just ten for now, reverting back to -1
  const resBlogs = await fetch(targetUrlBlogs);
  const blogPostsData = await resBlogs.json();

  // TODO
  // if switching gears,
  // exclude new dscrLoanStatePages
  // from being generated for now

  const paths = blogPostsData
    .filter((blogPost) => {
      return ![...calculatorURLs].includes(blogPost.URL.toString());
    })
    .map((blogPost) => {
      return {
        params: {
          URL: blogPost.URL.toString(),
        },
      };
    });

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const targetUrl = `${process.env.CMS_URL}/blog-posts?URL=${params.URL}`;
  const isDSCRLoanStatePage = dscrLoanStatePages.includes(params.URL);

  const res = await fetch(`${targetUrl}`);
  const data = await res.json();
  const postData = data[0];

  // no longer using
  const numOfBlogPosts = 115; // hard coded for simiplicity, actual number of blogs is 125
  const randomIntBelowMax = Math.floor(
    Math.random() * Math.floor(numOfBlogPosts)
  );
  const targetUrlBlogs = `${process.env.CMS_URL}/blog-posts?_limit=3&_start=${randomIntBelowMax}`;
  const resBlogs = await fetch(targetUrlBlogs);
  const BlogPostsData = await resBlogs.json();

  const targetUrlCapital = `${process.env.CMS_URL}/capital`;
  const resCapital = await fetch(targetUrlCapital);
  const CapitalData = await resCapital.json();

  // pre-process
  // Create a custom renderer
  const renderer = new marked.Renderer();

  // Override heading renderer to add tabindex property
  renderer.heading = function (text, level, raw, slugger) {
    const tabindex = level === 2 ? 'tabindex="0"' : ""; // Add tabindex only for h2
    const id = slugger.slug(text);
    return `<h${level} id="${id}" ${tabindex}>${text}</h${level}>`;
  };

  // appending lazy
  renderer.image = function (href, title, text) {
    return '<img src="' + href + '" alt="' + text + '" loading="lazy">';
  };

  renderer.link = function (href, title, text) {
    // Customize link rendering here
    return `<a href="${href}" title="${
      title || ""
    }" target="_blank" rel="noopener" style="color:#0000FF; text-decoration: underline;" onMouseOver="this.style.color='#00003D'"
      onMouseOut="this.style.color='#0000FF'" >${text}</a>`;
  };

  // setMarkedRenderer(renderer);
  const htmlData = marked(postData?.Body, { renderer: renderer });

  const htmlDoc = parse(htmlData);
  // Find all h2 elements
  const headings = htmlDoc.querySelectorAll("h2");

  const TOCData = headings.map((heading) => {
    return {
      title: heading.innerText,
      // depth: heading.nodeName.replace(/\D/g, ""),
      depth: 1,
      id: heading.getAttribute("id"),
    };
  });

  const url = params.URL;

  let urlStateName = url
    .split("-")
    .slice(2)
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");

  if (urlStateName === "Washington Dc") {
    urlStateName = "Washington DC";
  }

  let dscrLoanState = "";

  if (Object.keys(statesAbbreviations).includes(urlStateName)) {
    dscrLoanState = urlStateName;
  }

  const isAttorneyState =
    dscrLoanStateData.attorneyStates.includes(dscrLoanState);

  const calculatorDict = {
    // DSCR
    "dscr-calculator": "DSCR",
    "debt-service-ratio-calculator": "DSCR",
    "how-to-calculate-debt-service-coverage-ratio": "DSCR",
    "debt-service-calculator": "DSCR",
    "how-to-calculate-debt-service": "DSCR",
    "debt-service-coverage-ratio-calculator": "DSCR",
    "how-do-you-calculate-debt-service-coverage": "DSCR",
    "calculate-debt-service-coverage-ratio": "DSCR",
    "how-to-calculate-debt-service-coverage": "DSCR",
    "debt-service-coverage-calculation": "DSCR",
    "formula-for-calculating-debt-service-coverage-ratio": "DSCR",
    "annual-debt-service-calculator": "DSCR",
    "how-to-calculate-annual-debt-service": "DSCR",
    "how-to-calculate-debt-service-ratio": "DSCR",
    "debt-service-calculation": "DSCR",
    "calculating-debt-service": "DSCR",
    "how-do-you-calculate-debt-service": "DSCR",
    "calculate-debt-service-ratio": "DSCR",
    "calculating-debt-service-coverage-ratio": "DSCR",
    "debt-service-coverage-ratio-calculation": "DSCR",
    "how-do-you-calculate-debt-service-coverage-ratio": "DSCR",
    // DSCR loan
    "dscr-loan-calculator": "DSCR loan",
    "dscr-loan-calculator": "DSCR loan",
    "how-to-calculate-dscr": "DSCR loan",
    "calculate-dscr": "DSCR loan",
    "dscr-calculation": "DSCR loan",
    "calculate-dscr-ratio": "DSCR loan",
    "dscr-ratio-calculator": "DSCR loan",
    "calculate-dscr-ratio-for-rental-property": "DSCR loan",
    "how-to-calculate-dscr-ratio": "DSCR loan",
    "dscr-calculation-formula": "DSCR loan",
    "dscr-calculator-rental-property": "DSCR loan",
    "dscr-ratio-calculation": "DSCR loan",
    "dscr-mortgage-calculator": "DSCR loan",
    "dscr-cash-out-refinance-calculator": "DSCR loan",
    "how-do-you-calculate-net-operating-income-for-dscr-loans": "DSCR loan",
    "how-is-dscr-calculated": "DSCR loan",
    "calculating-dscr": "DSCR loan",
    "dscr-loans-calculator": "DSCR loan",
    "calculate-dscr-for-rental-property": "DSCR loan",
    "dscr-calculation-real-estate": "DSCR loan",
    "dscr-calculator-excel": "DSCR loan",
    "dscr-calculator-excel-download": "DSCR loan",
    // LAND LOAN
    "land-loan-calculator": "Land loan",
    "loan-calculator-for-land": "Land loan",
    "land-loans-calculator": "Land loan",
    "land-calculator-loan": "Land loan",
    "land-loan-calculator-with-down-payment": "Land loan",
    "land-loan-calculator-texas": "Land loan",
    "land-loan-payment-calculator": "Land loan",
    "farm-land-loan-calculator": "Land loan",
    "vacant-land-loan-calculator": "Land loan",
    "land-loan-calculator-payment": "Land loan",
    "loan-calculator-land": "Land loan",
    "loan-for-land-calculator": "Land loan",
    "land-loan-mortgage-calculator": "Land loan",
    "loan-for-land-purchase-calculator": "Land loan",
    "raw-land-loan-calculator": "Land loan",
    "land-payment-calculator": "Land loan",
    // ESCROW
    "escrow-calculator": "Escrow",
    "escrow-analysis-calculator": "Escrow",
    "initial-escrow-deposit-calculator": "Escrow",
    "escrow-account-calculator": "Escrow",
    "mortgage-and-tax-escrow-calculator": "Escrow",
    "aggregate-adjustment-calculator": "Escrow",
    "aggregate-escrow-calculator": "Escrow",
    "escrow-date-calculator": "Escrow",
    "amount-in-escrow": "Escrow",
    "property-tax-escrow-calculator": "Escrow",
    // CASH OFFER CALCULATOR
    "cash-offer-calculator": "Cash offer",
    "offer-calculator": "Cash offer",
    "calculate-cash-offer-real-estate": "Cash offer",
    "cash-offer-calculator-house": "Cash offer",
    "how-to-calculate-a-cash-offer-on-house-to-flip​": "Cash offer",
    // CASH ON CASH RETURN CALCULATOR
    "cash-on-cash-return-calculator": "Cash on cash return",
    "how-to-calculate-cash-on-cash-return": "Cash on cash return",
    "calculate-cash-on-cash-return": "Cash on cash return",
    "calculating-cash-on-cash-return": "Cash on cash return",
    "cash-on-cash-return-calculation": "Cash on cash return",
    "how-to-calculate-cash-on-cash-return-real-estate": "Cash on cash return",
    "calculate-cash-return-on-assets": "Cash on cash return",
    "cash-on-cash-return-calculator": "Cash on cash return",
    "how-do-you-calculate-cash-on-cash-return": "Cash on cash return",
    "cash-on-cash-return-calculator-real-estate": "Cash on cash return",
    "cash-on-cash-return-real-estate-calculator": "Cash on cash return",
    "calculating-cash-on-cash-return-for-real-estate": "Cash on cash return",
    "cash-on-cash-return-calculator-excel": "Cash on cash return",
    // CASH FLOW CALCULATOR (Better or full name is rental property cash flow calculator)
    "rental-property-cash-flow-calculator": "Cash flow",
    "cash-flow-calculator": "Cash flow",
    "rental-property-cash-flow-calculator": "Cash flow",
    "rental-cash-flow-calculator": "Cash flow",
    "calculating-cash-flow": "Cash flow",
    "calculate-cash-flow": "Cash flow",
    "cash-flow-calculation-formula": "Cash flow",
    "how-do-you-calculate-cash-flow": "Cash flow",
    "cash-flow-calculator-rental": "Cash flow",
    "calculate-cash-flows": "Cash flow",
    "calculate-the-cash-flow": "Cash flow",
    "cash-flow-rental-property-calculator": "Cash flow",
    // PRORATED RENT CALCULATOR
    "prorated-rent-calculator": "Prorated rent",
    "prorate-rent-calculator": "Prorated rent",
    "how-to-calculate-prorated-rent": "Prorated rent",
    "rent-prorate-calculator": "Prorated rent",
    "calculate-prorated-rent": "Prorated rent",
    "rent-proration-calculator": "Prorated rent",
    "how-do-i-calculate-prorated-rent": "Prorated rent",
    "how-do-you-calculate-prorated-rent": "Prorated rent",
    "prorated-rent-calculator-move-out": "Prorated rent",
    "calculating-prorated-rent": "Prorated rent",
    "prorated-rent-calculator-move-in": "Prorated rent",
    "free-prorated-rent-calculator": "Prorated rent",
    "prorate-rent-calculator-move-out": "Prorated rent",
    "move-out-prorated-rent-calculator": "Prorated rent",
    "rent-prorated-calculator": "Prorated rent",
    "prorated-move-out-rent-calculator": "Prorated rent",
    "prorated-rent-move-out-calculator": "Prorated rent",
    "how-is-prorated-rent-calculated": "Prorated rent",
    "prorate-calculator-for-rent": "Prorated rent",
    "prorate-rent-calculation": "Prorated rent",
    "prorated-rent-calculation": "Prorated rent",
    "prorate-calculator-rent": "Prorated rent",
    "prorate-rent-calculator-move-in": "Prorated rent",
    "prorated-rent-calculator-move-out": "Prorated rent",
    "prorated-rent-calculator-for-move-out": "Prorated rent",
    "prorated-rent-calculator-texas": "Prorated rent",
    "calculate-prorate-rent": "Prorated rent",
    "rent-calculator-prorated": "Prorated rent",
    "prorated-calculator-rent": "Prorated rent",
    "zillow-prorated-rent-calculator": "Prorated rent",
    "prorated-rent-moving-out-calculator": "Prorated rent",
    "prorated-calculator-for-rent": "Prorated rent",
    "how-prorated-rent-is-calculated": "Prorated rent",
    "how-to-calculate-prorate-rent": "Prorated rent",
    "prorated-rent-move-in-calculator": "Prorated rent",
    "how-to-prorate-rent-calculator": "Prorated rent",
    // RENTAL PROPERTY CASH FLOW CALCULATOR
    // "rental-property-cash-flow-calculator": "Rental property cash flow",
    // CASH OUT REFINANCE CALCULATOR
    // "cash-out-refinance-calculator": "Cash out refinance",
    // IRR CALCULATOR
    "irr-calculator": "IRR",
    "how-to-calculate-irr": "IRR",
    "calculate-irr-calculator": "IRR",
    "calculate-the-irr": "IRR",
    "how-do-you-calculate-irr": "IRR",
    "irr-calculation": "IRR",
    "calcul-irr": "IRR",
    "calculate-irr": "IRR",
    "calculating-irr": "IRR",
    "how-do-i-calculate-irr": "IRR",
    "how-is-irr-calculated": "IRR",
    "irr-calculated": "IRR",
    "formula-to-calculate-irr": "IRR",
    "calculation-of-irr": "IRR",
    "calculating-the-irr": "IRR",
    // CASH OUT REFINANCE CALCULATOR
    // "cash-out-refinance-calculator": "Cash out refinance",
    // PRORATED RENT CALCULATOR
    // "prorated-rent-calculator": "Prorated rent",
    // RENTAL PROPERPTY CALCULATOR
    // "rental-property-calculator": "Rental property",
    // CLOSING COST CALCULATOR
    "closing-cost-calculator": "Closing cost",
    "closing-costs-calculator": "Closing cost",
    "how-to-calculate-closing-costs": "Closing cost",
    "calculate-closing-costs": "Closing cost",
    "closing-cost-calculator-for-buyer": "Closing cost",
    "closing-cost-calculator-for-seller": "Closing cost",
    "how-are-closing-costs-calculated": "Closing cost",
    "seller-closing-cost-calculator": "Closing cost",
    "calculate-closing-costs-for-buyer": "Closing cost",
    "closing-costs-calculator-for-buyer": "Closing cost",
    "how-to-calculate-closing-cost": "Closing cost",
    "sellers-closing-costs-calculator": "Closing cost",
    "closing-cost-calculator-florida": "Closing cost",
    "how-can-i-calculate-closing-costs": "Closing cost",
    "buyers-closing-cost-calculator": "Closing cost",
    "how-do-you-calculate-closing-costs": "Closing cost",
    // DTI CALCULATOR
    "dti-calculator": "DTI",
    "how-to-calculate-dti": "DTI",
    "dti-ratio-calculator": "DTI",
    "calculate-dti": "DTI",
    "how-to-calculate-dti-ratio": "DTI",
    "mortgage-dti-calculator": "DTI",
    "dti-calculation": "DTI",
    "dti-ratio-mortgage-calculator": "DTI",
    "calculate-dti-ratio": "DTI",
    "dti-calculator-mortgage": "DTI",
    "dti-mortgage-calculator": "DTI",
    "dti-ratio-calculation": "DTI",
    "dti-calculator-for-mortgage": "DTI",
    "how-is-dti-calculated": "DTI",
    "how-to-calculate-dti-ratio-for-mortgage": "DTI",
    "how-to-calculate-your-dti-ratio": "DTI",
    "calculate-my-dti": "DTI",
    "how-to-calculate-dti-for-mortgage": "DTI",
    "calculating-dti": "DTI",
  };

  const calculatorType = calculatorDict?.[url] || "";

  return {
    props: {
      URL: url,
      postData,
      BlogPostsData,
      CapitalData,
      TOCData,
      htmlData,
      isDSCRLoanStatePage,
      dscrLoanState,
      isAttorneyState,
      calculatorType,
    },
  };
}

export default function Post({
  postData,
  URL,
  BlogPostsData,
  CapitalData,
  TOCData,
  htmlData,
  isDSCRLoanStatePage,
  dscrLoanState,
  isAttorneyState,
  calculatorType,
}) {
  const theme = useTheme();
  const router = useRouter();

  const { scrollTop } = useStore(AppStore);

  const [lowestRate, setLowestRate] = useState("");
  const [lowestSpread, setLowestSpread] = useState("");
  const [treasuryRate, setTreasuryRate] = useState("");

  const [TOCVisible, setTOCVisible] = useState(true);
  const [showQuoteStepper, setShowQuoteStepper] = useState(false);
  const [checked, setChecked] = useState([
    [false, false], // Checked status for parent 0
    [false, false, false], // Checked status for parent 1
    [false], // Checked status for parent 2
    [false, false, false], // 3
    [false, false, false], // 4
    [false, false, false], // 5
  ]);
  const [allChecked1, setAllChecked1] = useState(false);
  const [allChecked2, setAllChecked2] = useState(false);
  const [plugInTheNumbers, setPlugInTheNumbers] = useState(false);
  const [externalShowMeMyRate, setExternalShowMeMyRate] = useState(null);

  const handleToggle = (parentIndex, index) => () => {
    const newChecked = [...checked];
    newChecked[parentIndex][index] = !newChecked[parentIndex][index];
    setChecked(newChecked);
  };

  useEffect(() => {
    if (
      checked[0].every((subItem) => subItem) &&
      checked[1].every((subItem) => subItem) &&
      checked[2].every((subItem) => subItem)
    ) {
      setAllChecked1(true);
    } else {
      setAllChecked1(false);
    }

    if (
      checked[3].every((subItem) => subItem) &&
      checked[4].every((subItem) => subItem) &&
      checked[5].every((subItem) => subItem)
    ) {
      setAllChecked2(true);
    } else {
      setAllChecked2(false);
    }
  }, [checked]);

  const renderCTA = () => {
    let elToRender = [];

    switch (postData.URL) {
      default:
        // elToRender.push(<QuoteCTA postData={postData} scrollID={"top_cta"} />);
        elToRender.push(<Box />);
        break;
    }

    return elToRender;
  };

  const returnCalculatorComponent = (calculatorType) => {
    switch (calculatorType) {
      case "Land loan":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <LandLoanCalculator
              externalExampleCalc={{
                "Purchase price": 100000,
                "Loan to value": 75,
                "Amortization type": "Principal and interest",
                Term: 30,
                "Interest rate": 8,
                "Property tax": 2000,
                Insurance: 100,
                "Payment frequency": "Monthly",
                "Origination fee": 750,
                "Loan fees": 1000,
                "Title fees": 600,
                Escrow: 500,
                "Prepaid interest": 100,
                "Prepaid insurance": 200,
                "Calculation name": "5 Acre Land loan Example",
                "Address (optional)": "123 Rural Route, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
      case "Ground up construction":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <GroundUpCalculator
              URL={router.asPath.split("?")[0]}
              externalCalc={calc}
            />
          </Box>
        );
        break;
      case "DSCR":
        console.log("right_here");
        return (
          <Box
            key={`dscr_calculator`}
            sx={{
              padding: theme.spacing(0),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(0),
              },
            }}
          >
            <DSCRCalculator
              calculatorMode={calculatorType}
              URL={URL}
              CapitalData={CapitalData}
              externalCalc={false}
              externalExampleCalc={{
                "Monthly rent": 2450,
                "Annual taxes": 1100,
                "Annual insurance": 900,
                "Annual HOA": 213,
                "Loan purpose": "Purchase",
                "Purchase price": 190000,
                "Loan to value (LTV)": 0.75,
                "Loan amount": 142500,
                "Interest rate": 6.5,
                "Origination fee": 2000,
                "Loan fees": 1995,
                "Title fees": 600,
                Escrow: 500,
                "Prepaid interest": 95,
                "Prepaid insurance": 80,
                "Calculation name": "DSCR Example",
                "Address (optional)": "123 City Route, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
      case "DSCR loan":
        return (
          <Box
            key={`dscr_loan_calculator`}
            sx={{
              padding: theme.spacing(0),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(0),
              },
            }}
          >
            <DSCRCalculator
              calculatorMode={calculatorType}
              URL={URL}
              CapitalData={CapitalData}
              externalCalc={false}
              externalExampleCalc={{
                "Monthly rent": 2450,
                "Annual taxes": 1100,
                "Annual insurance": 900,
                "Annual HOA": 213,
                "Loan purpose": "Purchase",
                "Purchase price": 190000,
                "Loan to value (LTV)": 0.75,
                "Loan amount": 142500,
                "Interest rate": 6.5,
                "Origination fee": 2000,
                "Loan fees": 1995,
                "Title fees": 600,
                Escrow: 500,
                "Prepaid interest": 95,
                "Prepaid insurance": 80,
                "Calculation name": "DSCR Example",
                "Address (optional)": "123 City Route, Anytown, USA",
              }}
            />
          </Box>
        );
        break;

      case "Cash offer":
        return (
          <Box
            key={`cash_offer_calculator`}
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <CashOfferCalculator
              URL={URL}
              CapitalData={CapitalData}
              // externalCalc={calc}
              // externalExampleCalc={{
              //   "Purchase price": 250000,
              //   "Down payment": 20,
              //   "Monthly rent": 2000,
              //   "Annual property tax": 3000,
              //   "Annual insurance": 1200,
              //   "Monthly HOA": 250,
              //   "Annual maintenance": 2500,
              //   "Annual utilities": 1800,
              //   "Property management": 8,
              //   "Calculation name": "Cash on Cash Return Example",
              //   "Address (optional)": "456 Investment Ave, Anytown, USA",
              // }}
            />
          </Box>
        );
        break;
      case "Escrow":
        return (
          <Box
            key={`escrow_calculator_`}
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <EscrowCalculator
              URL={URL}
              CapitalData={CapitalData}
              // externalCalc={calc}
              externalExampleCalc={{
                "Loan start": dayjs().format("YYYY-MM"),
                "Home insurance": [
                  1725,
                  "annual",
                  2,
                  dayjs().format("YYYY-MM"),
                ],
                "Mortgage insurance": [
                  0,
                  "annual",
                  0,
                  dayjs().format("YYYY-MM"),
                ],
                "County tax": [
                  5504,
                  "semi-annual",
                  2,
                  dayjs().add(4, "month").format("YYYY-MM"),
                ],
                "City tax": [
                  0,
                  "semi-annual",
                  2,
                  dayjs().add(4, "month").format("YYYY-MM"),
                ],
                "School tax": [
                  0,
                  "semi-annual",
                  2,
                  dayjs().add(4, "month").format("YYYY-MM"),
                ],
                "Flood insurance": [
                  0,
                  "quarterly",
                  2,
                  dayjs().format("YYYY-MM"),
                ],
              }}
            />
          </Box>
        );
        break;
      case "Cash on cash return":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <CashOnCashReturnCalculator
              URL={URL}
              externalExampleCalc={{
                "Monthly rent": 2000,
                "Monthly mortgage": 1459,
                "Annual taxes": 1800,
                "Annual insurance": 1200,
                "Monthly reserve": 250,
                Downpayment: 35000,
                "Closing costs": 6861,
                "Calculation name": "Cash on Cash Return Example",
                "Address (optional)": "756 Investment Ave, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
      case "Cash flow":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <CashFlowCalculator
              URL={URL}
              externalExampleCalc={{
                "Monthly rent": 2250,
                "Monthly mortgage": 1159,
                "Annual taxes": 1300,
                "Annual insurance": 900,
                "Monthly reserve": 200,
                "Calculation name": "Cash on Cash Return Example",
                "Address (optional)": "756 Investment Ave, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
      case "Prorated rent":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <ProratedRentCalculator
              URL={URL}
              externalExampleCalc={{
                "Move-in date": dayjs().format("YYYY-MM-DD"),
                "Monthly rent": 2500,
                "Calculation name": "Prorated Rent Example",
                "Address (optional)": "789 Rental St, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
      // case "Rental property":
      //   return (
      //     <Box
      //       sx={{
      //         padding: theme.spacing(5),
      //         [theme.breakpoints.down("md")]: {
      //           padding: theme.spacing(1),
      //         },
      //       }}
      //     >
      //       <RentalPropertyCashFlowCalculator
      //         URL={URL}
      //         externalExampleCalc={{
      //           "Monthly rent": 2500,
      //           "Property tax": 4000,
      //           Insurance: 1500,
      //           "HOA fees": 200,
      //           Maintenance: 2000,
      //           "Property management": 10,
      //           "Mortgage payment": 1800,
      //           "Calculation name": "Rental Property Cash Flow Example",
      //           "Address (optional)": "321 Income Dr, Anytown, USA",
      //         }}
      //       />
      //     </Box>
      //   );
      //   break;
      case "Cash out refinance":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <CashOutRefinanceCalculator
              URL={URL}
              externalExampleCalc={{
                "Current home value": 400000,
                "Current loan balance": 250000,
                "New loan amount": 320000,
                "Interest rate": 5.5,
                "Loan term": 30,
                "Closing costs": 5000,
                "Calculation name": "Cash Out Refi Example",
                "Address (optional)": "654 Equity Ln, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
      case "IRR":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <IRRCalculator
              URL={URL}
              externalExampleCalc={{
                Downpayment: 50000,
                "Closing costs": 7500,
                "Monthly rent": 2500,
                "Monthly mortgage": 1500,
                "Annual taxes": 6000,
                "Annual insurance": 1200,
                "Monthly reserve": 200,
                "Holding period": 30,
                "Calculation name": "Sample Rental Property Investment",
                "Address (optional)":
                  "123 Investment Ave, Real Estate City, ST 12345",
              }}
            />
          </Box>
        );
        break;
      // case "Rental property":
      //   return (
      //     <Box
      //       sx={{
      //         padding: theme.spacing(5),
      //         [theme.breakpoints.down("md")]: {
      //           padding: theme.spacing(1),
      //         },
      //       }}
      //     >
      //       <RentalPropertyCalculator
      //         URL={URL}
      //         externalExampleCalc={{
      //           "Purchase price": 300000,
      //           "Down payment": 25,
      //           "Interest rate": 6,
      //           "Loan term": 30,
      //           "Monthly rent": 2800,
      //           "Property tax": 3600,
      //           Insurance: 1400,
      //           HOA: 150,
      //           Maintenance: 2400,
      //           "Vacancy rate": 5,
      //           "Calculation name": "Rental Property Analysis Example",
      //           "Address (optional)": "987 Rental Ave, Anytown, USA",
      //         }}
      //       />
      //     </Box>
      //   );
      //   break;
      case "Closing cost":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <ClosingCostsCalculator
              URL={URL}
              externalExampleCalc={{
                "Loan amount": 350000,
                "Loan type": "Conventional - 30 year mortgage",
                "Origination fee": 3500,
                "Loan fees": 1437,
                Appraisal: 575,
                "Title fees": 2611,
                Escrow: 1015,
                "Prepaid interest": 987,
                "Prepaid insurance": 1255,
                "Calculation name": "Closing cost example",
                "Address (optional)": "123 City Route, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
      case "DTI":
        return (
          <Box
            sx={{
              padding: theme.spacing(5),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <DTICalculator
              URL={URL}
              externalExampleCalc={{
                Salary: 12000,
                "Investment income": 500,
                "Other income": 0,
                Mortgage: 2000,
                Rent: 0,
                "Property tax": 400,
                Insurance: 150,
                HOA: 300,
                "Credit card payment": 300,
                "Student loan payment": 350,
                "Auto loan payment": 400,
                "Other liability payments": 200,
                "Calculation name": "DTI Ratio Example",
                "Address (optional)": "123 Main St, Anytown, USA",
              }}
            />
          </Box>
        );
        break;
    }
  };

  return (
    <>
      <NextSeo
        title={postData?.MetaTitle}
        description={postData?.MetaDescription}
        image={
          publicMetaImages[router.asPath.split("?")[0]]
            ? `${process.env.absoluteUrl}${
                publicMetaImages[router.asPath.split("?")[0]]
              }`
            : Object.keys(postData?.MetaImage) !== 0
            ? `${process.env.CMS_URL}${postData?.MetaImage.url}`
            : `${process.env.absoluteUrl}/omlogo_placeholder.png`
        }
        openGraph={{
          url: `${process.env.absoluteUrl}/blog/${URL}`,
          title: postData?.MetaTitle,
          description: postData?.MetaDescription,
          images: [
            {
              url: publicMetaImages[router.asPath.split("?")[0]]
                ? `${process.env.absoluteUrl}${
                    publicMetaImages[router.asPath.split("?")[0]]
                  }`
                : postData?.MetaImage?.length > 0
                ? `${process.env.CMS_URL}${postData?.MetaImage[0].url}`
                : `${process.env.absoluteUrl}/omlogo_placeholder.png`,
              alt: postData?.MetaTitle,
            },
          ],
          site_name: "OfferMarket.us",
        }}
        twitter={{
          handle: "@OfferMarketUS",
          site: "@OfferMarketUS",
          cardType: "summary_large_image",
        }}
      />
      <Head>
        <meta
          property="twitter:image"
          content={
            publicMetaImages[router.asPath.split("?")[0]]
              ? `${process.env.absoluteUrl}${
                  publicMetaImages[router.asPath.split("?")[0]]
                }`
              : postData?.MetaImage?.length > 0
              ? `${process.env.CMS_URL}${postData?.MetaImage[0].url}`
              : `${process.env.absoluteUrl}/omlogo_placeholder.png`
          }
        />
      </Head>

      <Box
        sx={{
          display: "none",
          backgroundColor: "white",
          [theme.breakpoints.down("md")]: {
            // UPDATE, won't show TOC on mobile
            // to see if this improves conversion
            // display: "flex",
            display: "none",
            width: "100%",
            height: "4.5em",
            position: "sticky",
            flexDirection: "column",
            zIndex: 3,
            top: 0,
            backgroundColor: "white !important",
            boxShadow: "0 1px 3px rgb(0 0 0 / 12%), 0 1px 2px rgb(0 0 0 / 24%)",
          },
        }}
      >
        {TOCVisible && TOCData.length > 0 && (
          <Box sx={{ display: "flex", margin: "auto", width: "100%" }}>
            <TableOfContent TOCData={TOCData} />
          </Box>
        )}
      </Box>
      <MuiMarkupWrap>
        {!isDSCRLoanStatePage && (
          <BlogSidebarActions TOCData={TOCData} url={postData.URL} />
        )}

        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: theme.spacing("2.5em"),
              margin: "auto",
              marginTop: theme.spacing(6),
              [theme.breakpoints.down("md")]: {
                fontSize: theme.spacing("1.5em"),
                margin: theme.spacing(1, 0, 1, 0),
              },
            }}
            variant={"h1"}
          >
            {postData?.Title}
          </Typography>
          {!isDSCRLoanStatePage && (
            <Box
              sx={{
                margin: theme.spacing(2, 9, 1, 9),
                [theme.breakpoints.down("md")]: { margin: 0 },
              }}
            >
              {renderCTA()}
            </Box>
          )}

          {isDSCRLoanStatePage && (
            <Box sx={{ margin: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  margin: theme.spacing(6, 0, 3, 0),
                  [theme.breakpoints.down("md")]: {
                    flexDirection: "column",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "40%",
                    [theme.breakpoints.down("md")]: {
                      margin: theme.spacing(1, 0, 0, 0),
                      width: "100%",
                      order: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: theme.spacing(0.5),
                      position: "relative",
                      overflow: "hidden",
                      minHeight: theme.spacing(46),
                      [theme.breakpoints.down("md")]: {
                        height: theme.spacing(30),
                      },
                    }}
                  >
                    <Box
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: "hidden",
                      }}
                    >
                      <Image
                        // since above fold picture, load ASAP
                        // loading={"eager"} // Recommendation: This property is only meant for advanced use cases. Switching an image to load with eager will normally hurt performance. We recommend using the priority property instead, which will eagerly preload the image.
                        priority={true}
                        alt="Image of a suburban home in a USA neighborhood"
                        layout="fill"
                        objectFit="cover"
                        quality={25}
                        src={`https://cms.offermarket.us/${dscrLoanStateData[dscrLoanState]?.image1}`}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: "59%",
                    margin: theme.spacing(0, 0, 0, 0),
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    [theme.breakpoints.down("md")]: {
                      width: "100%",
                      order: 2,
                    },
                  }}
                >
                  <Alert
                    severity="info"
                    sx={{
                      height: "100%",
                      "& .MuiAlert-message": {
                        display: "flex",
                        flexDirection: "column",
                        padding: `${theme.spacing(0.5, 0, 0, 0)} !important`,
                      },
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1, 0, 0, 0),
                        "& .MuiAlert-message": {
                          padding: `${theme.spacing(10, 0, 0, 0)} !important`,
                          width: "100% !important",
                        },
                      },
                    }}
                  >
                    <Typography>
                      Why choose <b>OfferMarket</b> for your{" "}
                      <b>DSCR loan in {dscrLoanState}?</b>
                      <br />
                      <br />
                    </Typography>

                    <Box
                      sx={{
                        margin: theme.spacing("auto", 0),
                      }}
                    >
                      <Typography
                        as="ul"
                        sx={{
                          [theme.breakpoints.down("md")]: {
                            padding: theme.spacing(0, 0, 0, 2),
                          },
                        }}
                      >
                        {dscrLoanStateData[dscrLoanState]?.whyUsList.map(
                          (item) => {
                            return (
                              <>
                                <li
                                  dangerouslySetInnerHTML={{
                                    __html: dscrLoanStateData[item](
                                      dscrLoanState,
                                      isAttorneyState
                                    ),
                                  }}
                                />
                                <br />
                              </>
                            );
                          }
                        )}
                      </Typography>
                    </Box>
                  </Alert>
                </Box>
              </Box>
              <Box sx={{ width: "100%" }}>
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    display: "flex",
                    margin: theme.spacing(10, 0, 0, 0),
                    "& .MuiAlert-message": {
                      width: "100% !important",
                      [theme.breakpoints.down("md")]: {
                        padding: 0,
                      },
                    },
                    [theme.breakpoints.down("md")]: {
                      margin: theme.spacing(1, 0, 0, 0),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: theme.spacing(2, 1),
                      width: "95%",
                      [theme.breakpoints.down("md")]: {
                        margin: 0,
                        flexDirection: "column",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: theme.spacing(4),
                        }}
                      >
                        Apply for a<b> DSCR Loan in {dscrLoanState}</b>
                      </Typography>
                      <Typography
                        component="h2"
                        sx={{
                          display: "flex",
                          width: "100% !important",
                          fontSize: theme.spacing(1.5),
                          textAlign: "right",
                          [theme.breakpoints.down("md")]: {
                            display: "block",
                          },
                        }}
                      >
                        * Takes 2 minutes, no credit pull
                      </Typography>
                    </Box>
                    <Button
                      sx={{
                        margin: theme.spacing(1, 2, 1, "auto"),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 2, 1, "auto"),
                        },
                      }}
                      onClick={() => {
                        setShowQuoteStepper(!showQuoteStepper);
                      }}
                      variant="contained"
                      color="success"
                    >
                      Start{" "}
                      <Box
                        sx={{
                          padding: theme.spacing(0, 0, 0, 1),
                          fontSize: theme.spacing(2),
                          animation: `${keyframes`
                        0% {
                          transform: scale(1.0);
                        }
                        50% {
                          transform: scale(1.333);
                        }
                        100% {
                          transform: scale(1.0);
                        }
                      `} 3s infinite ease`,
                        }}
                      >
                        {" "}
                        🚀
                      </Box>
                    </Button>
                  </Box>
                </Alert>
              </Box>
              <Collapse in={showQuoteStepper}>
                <Box
                  sx={{
                    margin: theme.spacing(2, "30%", 0, "30%"),
                    [theme.breakpoints.down("md")]: {
                      margin: 0,
                    },
                  }}
                >
                  <QuoteStepper scrollID={"top_cta"} />
                </Box>
              </Collapse>

              <Box sx={{ margin: theme.spacing(10, 0, 3, 0) }}>
                <Typography
                  variant="h2"
                  sx={{
                    margin: theme.spacing(3, 0, 1, 0),
                    fontSize: theme.spacing(4),
                  }}
                >
                  DSCR Loan {dscrLoanState} Example Deal
                </Typography>
                <Alert
                  severity="info"
                  sx={{
                    margin: theme.spacing(1, 0),
                    fontSize: theme.spacing(2),
                  }}
                >
                  We recently funded a deal with the following numbers. Click
                  "Plug in" to see a calculation of <b>cashflow</b>,<b> DSCR</b>{" "}
                  and <b>cash to close</b>
                </Alert>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      [theme.breakpoints.down("md")]: {
                        flexDirection: "column",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "80%",
                        [theme.breakpoints.down("md")]: {
                          width: "100%",
                          margin: "auto",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: theme.spacing(0.5),
                          position: "relative",
                          height: theme.spacing(46),
                          overflow: "hidden",
                          margin: theme.spacing(0, 3, 0, 0),
                          [theme.breakpoints.down("md")]: {
                            height: theme.spacing(32),
                            margin: "auto",
                          },
                        }}
                      >
                        <Image
                          alt="Image of an abandoned house"
                          layout="fill"
                          quality={25}
                          objectFit="cover"
                          src={`https://cms.offermarket.us/${dscrLoanStateData[dscrLoanState]?.image2}`}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: "52%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        [theme.breakpoints.down("md")]: {
                          width: "100%",
                          margin: theme.spacing(1, 0, 0, 0),
                        },
                      }}
                    >
                      <Alert
                        severity="info"
                        variant="outlined"
                        sx={{
                          margin: theme.spacing(0, "auto", 1, "auto"),
                          width: theme.spacing(30),
                          "& .MuiAlert-message": {
                            width: "100%",
                          },
                          [theme.breakpoints.down("md")]: { width: "100%" },
                        }}
                      >
                        <b>Borrower</b>:
                        <Box
                          sx={{
                            margin: theme.spacing(1, 0, 0, 0),
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              margin: theme.spacing(0, 0, 0, 0),
                              display: "flex",
                              flexDirection: "row",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>Credit score:</Box>{" "}
                            <Box>
                              {
                                dscrLoanStateData[dscrLoanState]
                                  ?.dscrCalculation["creditScore"]
                              }
                            </Box>
                          </Box>
                        </Box>
                      </Alert>
                      <Alert
                        severity="info"
                        variant="outlined"
                        sx={{
                          margin: theme.spacing(1, "auto"),
                          width: theme.spacing(30),
                          "& .MuiAlert-message": {
                            width: "100%",
                          },
                          [theme.breakpoints.down("md")]: { width: "100%" },
                        }}
                      >
                        <b>Investment Property</b>:
                        <Box
                          sx={{
                            margin: theme.spacing(1, 0, 0, 0),
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              margin: theme.spacing(2, 0, 2, 0),
                              display: "flex",
                              flexDirection: "row",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>Price:</Box>
                            <Box>
                              $
                              {formatMoney(
                                dscrLoanStateData[dscrLoanState]
                                  ?.dscrCalculation["price"],
                                0
                              )}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              margin: theme.spacing(0, 0, 0, 0),
                              display: "flex",
                              flexDirection: "row",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>Rent:</Box>{" "}
                            <Box>
                              {" "}
                              $
                              {formatMoney(
                                dscrLoanStateData[dscrLoanState]
                                  ?.dscrCalculation["Monthly rent"],
                                0
                              )}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              margin: theme.spacing(0, 0, 0, 0),
                              display: "flex",
                              flexDirection: "row",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>Taxes:</Box>{" "}
                            <Box>
                              {" "}
                              $
                              {formatMoney(
                                dscrLoanStateData[dscrLoanState]
                                  ?.dscrCalculation["Annual taxes"],
                                0
                              )}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              margin: theme.spacing(0, 0, 0, 0),
                              display: "flex",
                              flexDirection: "row",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>Insurance:</Box>{" "}
                            <Box>
                              {" "}
                              $
                              {formatMoney(
                                dscrLoanStateData[dscrLoanState]
                                  ?.dscrCalculation["Annual insurance"],
                                0
                              )}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              margin: theme.spacing(0, 0, 0, 0),
                              display: "flex",
                              flexDirection: "row",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>HOA:</Box>{" "}
                            <Box>
                              {" "}
                              $
                              {formatMoney(
                                dscrLoanStateData[dscrLoanState]
                                  ?.dscrCalculation["Annual HOA"],
                                0
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Alert>
                      <Alert
                        severity="info"
                        variant="outlined"
                        sx={{
                          width: theme.spacing(30),
                          [theme.breakpoints.down("md")]: { width: "100%" },
                          margin: theme.spacing(1, "auto", 0, "auto"),
                          "& .MuiAlert-message": {
                            width: "100% !important",
                          },
                          "& .MuiAlert-icon": {
                            display: "none",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                          <Button
                            sx={{
                              display: "flex",
                              margin: theme.spacing(0, 0, 0, "auto"),
                              width: theme.spacing(30),
                              [theme.breakpoints.down("md")]: { width: "100%" },
                            }}
                            onClick={() => {
                              setPlugInTheNumbers(true);
                              setTimeout(() => {
                                scroller(`title_Monthly cashflow`, false, -250);
                              }, 1000);
                            }}
                            variant="contained"
                            color="success"
                          >
                            Calculate DSCR
                          </Button>
                        </Box>
                      </Alert>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ margin: theme.spacing(2, 0) }}>
                <Typography sx={{ fontSize: theme.spacing(5) }} variant="h2">
                  DSCR Loan {dscrLoanState} calculation
                </Typography>
                <Box
                  sx={{
                    minHeight: "100vh",
                    width: "100%",
                    display: "flex",
                    margin: theme.spacing(3, "auto"),
                    [theme.breakpoints.down("md")]: {
                      width: "100%",
                    },
                  }}
                >
                  <DSCRCalculator
                    calculatorMode={"DSCR loan"}
                    URL={URL}
                    CapitalData={CapitalData}
                    externalCalc={false}
                    externalShowMeMyRate={externalShowMeMyRate}
                    plugInTheNumbers={plugInTheNumbers}
                    externalExampleCalc={
                      dscrLoanStateData[dscrLoanState]?.dscrCalculation
                    }
                  />
                </Box>
              </Box>
              <Box sx={{ margin: theme.spacing(3, 0) }}>
                <Typography
                  variant="h2"
                  sx={{
                    margin: theme.spacing(3, 0),
                    fontSize: theme.spacing(4),
                  }}
                >
                  DSCR Loan Requirements
                </Typography>
                <Alert severity="info">
                  If you get all the checkmarks, you <b>qualify</b>!
                </Alert>
                <Box>
                  {[0, 1, 2].map((parentIndex, index) => {
                    return (
                      <List
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                        key={`${parentIndex}_parent_list_item_${index}`}
                      >
                        <ListItem
                          key={`${parentIndex}_heading_list_item_${index}`}
                          onClick={() => {}}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                edge="start"
                                checked={checked[parentIndex].every(
                                  (item) => item
                                )}
                                tabIndex={-1}
                                disableRipple
                                sx={{
                                  display: checked[parentIndex].every(
                                    (item) => item
                                  )
                                    ? "block"
                                    : "none",
                                }}
                                color={
                                  checked[parentIndex].every((item) => item)
                                    ? "success"
                                    : "primary"
                                }
                              />
                            }
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: `${theme.spacing(2.5)} !important`,
                              },
                            }}
                            label={
                              {
                                0: "Borrower requirements",
                                1: "Deal requirements",
                                2: "Downpayment requirements",
                              }[parentIndex]
                            }
                          />
                        </ListItem>
                        {{
                          0: [
                            "Is credit score above 660 (best terms for 720+) on a (hard pull) tri-merge report for the borrower or co-guarantor?",
                            "Do you have an LLC? OfferMarket can guide you toward LLC creation if necessary.",
                          ],
                          1: [
                            "Does the deal have a DSCR above 1.11?",
                            `Not owner-occupied?`,
                            "Not rural?",
                          ],
                          2: [
                            "Do you have at least 20% downpayment (cash to close) for the deal?",
                          ],
                        }[parentIndex].map((task, index) => (
                          <ListItem
                            key={`${parentIndex}_${index}_child_list_item`}
                            sx={{ padding: 0 }}
                          >
                            <ButtonBase
                              onClick={handleToggle(parentIndex, index)}
                              sx={{
                                textAlign: "left",
                                margin: theme.spacing(0),
                                padding: theme.spacing(1, 2),
                                borderRadius: theme.spacing(1),
                              }}
                            >
                              <Checkbox
                                edge="start"
                                checked={checked[parentIndex][index]}
                                tabIndex={-1}
                                disableRipple
                                color={
                                  checked[parentIndex].every((item) => item)
                                    ? "success"
                                    : "primary"
                                }
                              />
                              <ListItemText primary={task} />
                            </ButtonBase>
                          </ListItem>
                        ))}
                      </List>
                    );
                  })}
                </Box>
                {allChecked1 && (
                  <>
                    <Alert
                      sx={{
                        margin: theme.spacing(3, 0, 0, 0),
                        "& .MuiAlert-message": {
                          width: "100% !important",
                        },
                      }}
                      severity="success"
                      variant="outlined"
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          margin: theme.spacing(2, 1),
                          [theme.breakpoints.down("md")]: {
                            margin: 0,
                            flexDirection: "column",
                          },
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            sx={{
                              fontSize: theme.spacing(4),
                            }}
                          >
                            You qualify for a{" "}
                            <b>DSCR Loan in {dscrLoanState}</b>
                          </Typography>
                          <Typography
                            component="h2"
                            sx={{
                              display: "flex",
                              width: "100% !important",
                              fontSize: theme.spacing(1.5),
                              textAlign: "right",
                              [theme.breakpoints.down("md")]: {
                                display: "block",
                              },
                            }}
                          >
                            * Takes 2 minutes, no credit pull
                          </Typography>
                        </Box>
                        <Button
                          sx={{
                            margin: theme.spacing(1, 2, 1, "auto"),
                            [theme.breakpoints.down("md")]: {
                              margin: theme.spacing(1, 2, 1, "auto"),
                            },
                          }}
                          onClick={() => {
                            setShowQuoteStepper(!showQuoteStepper);
                          }}
                          variant="contained"
                          color="success"
                        >
                          Start{" "}
                          <Box
                            sx={{
                              padding: theme.spacing(0, 0, 0, 1),
                              fontSize: theme.spacing(2),
                              margin: theme.spacing(0, 1, 0, 0),
                              animation: `${keyframes`
                        0% {
                          transform: scale(1.0);
                        }
                        50% {
                          transform: scale(3.1);
                        }
                        100% {
                          transform: scale(1.0);
                        }
                      `} 3s infinite ease`,
                            }}
                          >
                            {" "}
                            🚀
                          </Box>
                        </Button>
                      </Box>
                    </Alert>
                    <Collapse in={showQuoteStepper}>
                      <Box
                        sx={{
                          margin: theme.spacing(2, "30%", 0, "30%"),
                          [theme.breakpoints.down("md")]: {
                            margin: 0,
                          },
                        }}
                      >
                        <QuoteStepper scrollID={"middle_cta"} />
                      </Box>
                    </Collapse>
                  </>
                )}
              </Box>
              <Box sx={{ margin: theme.spacing(3, 0) }}>
                <Typography
                  variant="h2"
                  sx={{
                    margin: theme.spacing(3, 0),
                    fontSize: theme.spacing(4),
                  }}
                >
                  DSCR Loan {dscrLoanState} Common Questions
                </Typography>
                <Alert severity="info">
                  Search common questions about our{" "}
                  <b>DSCR loans in {dscrLoanState} </b>
                </Alert>{" "}
                <Box>
                  {TOCData.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        margin: "auto",
                        width: "100%",
                        margin: theme.spacing(1, 0, 0, 0),
                      }}
                    >
                      <TableOfContent
                        TOCData={TOCData}
                        customHeading={" "}
                        mode="search"
                      />
                    </Box>
                  )}
                </Box>
                <MuiMarkupWrap
                  customMargin={"0"}
                  customH2Size={theme.spacing(3)}
                >
                  {postData?.Body && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: htmlData,
                      }}
                    />
                  )}
                </MuiMarkupWrap>
              </Box>
            </Box>
          )}

          {calculatorType && (
            <Box>{returnCalculatorComponent(calculatorType)}</Box>
          )}

          {!isDSCRLoanStatePage && postData?.Body && htmlData && (
            <Box sx={{ minHeight: "100vh" }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: htmlData,
                }}
              />
            </Box>
          )}
        </Box>
      </MuiMarkupWrap>
      {URL === "dscr-loan-interest-rates" && (
        <Box
          sx={{
            margin: theme.spacing(20, "22%", 40, "20%"),
            height: theme.spacing(50),
            [theme.breakpoints.down("md")]: {
              margin: theme.spacing(3, 3, 0, 0),
            },
          }}
        >
          <Box sx={{ display: "flex" }}>
            <Typography
              variant="h2"
              sx={{ margin: theme.spacing(2, 0), fontSize: theme.spacing(4) }}
            >
              DSCR Loan Interest Rate Chart
            </Typography>
            <Typography
              sx={{
                margin: theme.spacing("auto", 0, "auto", "auto"),
                fontSize: theme.spacing(1.5),
              }}
            >
              Chart by{" "}
              <Link
                sx={{ color: theme.palette.highContrastLink.main }}
                rel="noopener"
                target="_blank"
                href="https://www.tradingview.com"
              >
                TradingView
              </Link>
            </Typography>
          </Box>
          <InterestRateChart
            setLowestRate={setLowestRate}
            setLowestSpread={setLowestSpread}
            setTreasuryRate={setTreasuryRate}
          />
        </Box>
      )}
      <MuiIndexMarkupWrap>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
            },
          }}
        >
          {BlogPostsData &&
            BlogPostsData.map((blogPost, index) => {
              return (
                <Box
                  sx={{
                    margin: theme.spacing(3, 2),
                    width: "23%",
                    [theme.breakpoints.down("md")]: {
                      width: "auto",
                    },
                  }}
                >
                  <BlogCard post={blogPost} parentUrl={"/blog"} />
                </Box>
              );
            })}
        </Box>
      </MuiIndexMarkupWrap>
      <QuoteTeaser
        capitalData={CapitalData}
        postData={postData}
        url={postData.URL}
      />
    </>
  );
}
